import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
  makeStyles,
  ButtonBase,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Paper,
} from '@material-ui/core';
import UIDialogTitle from '../../UI/Dialog/Title/UIDialogTitle';
import { formattedCurrency } from '../../../utils/utils';

function extractVolumeOrWeight(name, pUnit) {
  if (name) {
    const match = name.match(/(\d+(?:[.,]\d+)?)\s*(ml|l|litros?|g|kg|gramas?|quilos?)\b/i);
    if (match) {
      const rawNum = parseFloat(match[1].replace(',', '.'));
      const unit = match[2].toLowerCase();
      if (unit.startsWith('l')) {
        return { total: rawNum * 1000, unit: 'ml', displayValue: rawNum, displayUnit: 'L' };
      }
      if (unit === 'kg' || unit.startsWith('quilo')) {
        return { total: rawNum * 1000, unit: 'g', displayValue: rawNum, displayUnit: 'kg' };
      }
      return { total: rawNum, unit: unit.startsWith('m') ? 'ml' : 'g', displayValue: rawNum, displayUnit: unit.startsWith('m') ? 'ml' : 'g' };
    }
  }
  if (pUnit) {
    const u = pUnit.trim().toLowerCase();
    if (u === 'l' || u === 'lt' || u === 'litro' || u === 'litros') {
      return { total: 1000, unit: 'ml', displayValue: 1, displayUnit: 'L' };
    }
    if (u === 'ml') {
      return { total: 1000, unit: 'ml', displayValue: 1000, displayUnit: 'ml' };
    }
    if (u === 'kg' || u === 'kilo' || u === 'quilo') {
      return { total: 1000, unit: 'g', displayValue: 1, displayUnit: 'kg' };
    }
    if (u === 'g' || u === 'gr' || u === 'grama' || u === 'gramas') {
      return { total: 1000, unit: 'g', displayValue: 1000, displayUnit: 'g' };
    }
  }
  return null;
}

function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

function simplifyFraction(num, den) {
  if (!num || !den) return null;
  const a = Math.round(num);
  const b = Math.round(den);
  if (a <= 0 || b <= 0) return null;
  const divisor = gcd(a, b);
  const sNum = a / divisor;
  const sDen = b / divisor;
  if (sDen === 1) return `${sNum}`;
  return `${sNum}/${sDen}`;
}

const useStyles = makeStyles((theme) => ({
  segmentedContainer: {
    display: 'flex',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 3,
    marginBottom: theme.spacing(2),
    border: '1px solid #e2e8f0',
  },
  segmentedButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 6,
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#64748b',
    transition: 'all 0.2s ease',
  },
  segmentedButtonActive: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: 6,
    fontSize: '0.875rem',
    fontWeight: 600,
    backgroundColor: '#ffffff',
    color: '#17497F',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  summaryBox: {
    backgroundColor: '#f8fafc',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  quickChip: {
    margin: '3px 4px',
    fontWeight: 500,
    fontSize: '0.8rem',
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: theme.spacing(1),
  },
}));

const ServiceInputCalculatorModal = ({
  open,
  onClose,
  item,
  initialAmount,
  isEditing = false,
  onApply,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [activeTab, setActiveTab] = useState('yield'); // 'yield' | 'measure'

  // Tab 1: Yield / Fractions
  const [portionCount, setPortionCount] = useState(1);
  const [totalYield, setTotalYield] = useState(12);

  // Tab 2: Weight / Volume Measure
  const [containerAmount, setContainerAmount] = useState(1000);
  const [containerUnit, setContainerUnit] = useState('g');
  const [recipeAmount, setRecipeAmount] = useState(50);
  const [recipeUnit, setRecipeUnit] = useState('g');

  const itemName = item?.name || '';
  const itemUnit = item?.unit || '';
  const itemCost = parseFloat(item?.cost || 0);

  // Initialize/prefill when modal opens or item changes
  useEffect(() => {
    if (open && item) {
      const extracted = extractVolumeOrWeight(itemName, itemUnit);
      const rawInit = initialAmount !== undefined && initialAmount !== null ? initialAmount : item.amount;

      if (rawInit && typeof rawInit === 'string' && rawInit.includes('/')) {
        const parts = rawInit.split('/');
        if (parts.length === 2) {
          const num = parseFloat(parts[0]);
          const den = parseFloat(parts[1]);
          if (!isNaN(num) && !isNaN(den) && den > 0) {
            setActiveTab('yield');
            setPortionCount(num);
            setTotalYield(den);
            return;
          }
        }
      }

      const numAmount = parseFloat(rawInit);

      if (extracted) {
        setActiveTab('measure');
        setContainerAmount(extracted.displayValue);
        setContainerUnit(extracted.displayUnit);

        if (numAmount > 0) {
          const basePortion = numAmount * extracted.total;
          if (extracted.unit === 'ml') {
            if (basePortion >= 1000 && basePortion % 1000 === 0) {
              setRecipeAmount(basePortion / 1000);
              setRecipeUnit('L');
            } else {
              setRecipeAmount(parseFloat(basePortion.toFixed(2)));
              setRecipeUnit('ml');
            }
          } else {
            if (basePortion >= 1000 && basePortion % 1000 === 0) {
              setRecipeAmount(basePortion / 1000);
              setRecipeUnit('kg');
            } else {
              setRecipeAmount(parseFloat(basePortion.toFixed(2)));
              setRecipeUnit('g');
            }
          }
        } else {
          if (extracted.unit === 'ml') {
            setRecipeAmount(50);
            setRecipeUnit('ml');
          } else {
            setRecipeAmount(30);
            setRecipeUnit('g');
          }
        }
      } else {
        setActiveTab('yield');
        if (numAmount > 0 && numAmount < 1) {
          const approxYield = Math.round(1 / numAmount);
          if (approxYield > 0 && Math.abs(1 / approxYield - numAmount) < 0.001) {
            setPortionCount(1);
            setTotalYield(approxYield);
          } else {
            setPortionCount(numAmount);
            setTotalYield(1);
          }
        } else if (numAmount >= 1) {
          setPortionCount(numAmount);
          setTotalYield(1);
        } else {
          setPortionCount(1);
          setTotalYield(12);
        }
      }
    }
  }, [open, item, itemName, itemUnit, initialAmount]);

  // Calculations for Yield Mode
  const yieldResult = useMemo(() => {
    const count = parseFloat(portionCount) || 0;
    const yld = parseFloat(totalYield) || 0;
    if (count <= 0 || yld <= 0) {
      return { fractionStr: '0', decimal: 0, cost: 0 };
    }
    const simplified = simplifyFraction(count, yld);
    const decimal = count / yld;
    return {
      fractionStr: simplified || `${count}/${yld}`,
      decimal,
      cost: itemCost * decimal,
    };
  }, [portionCount, totalYield, itemCost]);

  // Calculations for Measure Mode (Weight / Volume)
  const measureResult = useMemo(() => {
    const cVal = parseFloat(containerAmount) || 0;
    const rVal = parseFloat(recipeAmount) || 0;
    if (cVal <= 0 || rVal <= 0) {
      return { fractionStr: '0', decimal: 0, cost: 0, isValid: false };
    }

    // Convert container to base unit (ml or g)
    let cBase = cVal;
    if (containerUnit === 'L' || containerUnit === 'kg') {
      cBase = cVal * 1000;
    }

    // Convert recipe portion to base unit (ml or g)
    let rBase = rVal;
    if (recipeUnit === 'L' || recipeUnit === 'kg') {
      rBase = rVal * 1000;
    }

    // Check unit compatibility (weight with weight, volume with volume)
    const isVolume = (u) => u === 'ml' || u === 'L';
    const isWeight = (u) => u === 'g' || u === 'kg';
    const compatible = (isVolume(containerUnit) && isVolume(recipeUnit)) || (isWeight(containerUnit) && isWeight(recipeUnit));

    if (!compatible || cBase <= 0) {
      return { fractionStr: '0', decimal: 0, cost: 0, isValid: false, incompatible: true };
    }

    const decimal = rBase / cBase;
    const simplified = simplifyFraction(rBase, cBase);

    // Format fraction nicely: if integer or simple fraction use it, else 4 decimal places
    let displayFraction = simplified;
    if (!simplified || simplified.includes('/')) {
      const parts = (simplified || '').split('/');
      if (parts.length === 2 && parseInt(parts[1], 10) > 100) {
        displayFraction = decimal.toFixed(4).replace(/\.?0+$/, '');
      }
    }

    return {
      fractionStr: displayFraction || decimal.toFixed(4).replace(/\.?0+$/, ''),
      decimal,
      cost: itemCost * decimal,
      isValid: true,
      incompatible: false,
    };
  }, [containerAmount, containerUnit, recipeAmount, recipeUnit, itemCost]);

  const activeResult = activeTab === 'yield' ? yieldResult : measureResult;

  const handleApply = () => {
    if (activeResult && activeResult.decimal > 0) {
      onApply(activeResult.fractionStr, activeResult);
      onClose();
    }
  };

  const isWeightSelected = recipeUnit === 'g' || recipeUnit === 'kg' || containerUnit === 'g' || containerUnit === 'kg';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="calculator-dialog-title"
    >
      <UIDialogTitle id="calculator-dialog-title" onClose={onClose}>
        {isEditing ? 'Ajustar Insumo / Quantidade' : 'Calculadora de Insumo / Porção'}
      </UIDialogTitle>

      <DialogContent dividers style={{ padding: isMobile ? 12 : 20 }}>
        {/* Item context banner */}
        <Box mb={2} p={1.5} bgcolor="#f8fafc" border="1px solid #e2e8f0" borderRadius={8}>
          <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e293b' }} noWrap>
            {itemName || 'Insumo selecionado'}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={0.5} flexWrap="wrap">
            <Typography variant="body2" style={{ color: '#64748b' }}>
              Custo cadastrado: <strong>{formattedCurrency(itemCost)}</strong>
            </Typography>
            {itemUnit && (
              <Chip
                label={`Unidade: ${itemUnit}`}
                size="small"
                style={{ backgroundColor: '#e2e8f0', color: '#334155', fontWeight: 500 }}
              />
            )}
          </Box>
        </Box>

        {/* Segmented Mode Selector */}
        <div className={classes.segmentedContainer}>
          <ButtonBase
            className={activeTab === 'yield' ? classes.segmentedButtonActive : classes.segmentedButton}
            onClick={() => setActiveTab('yield')}
          >
            Rendimento / Frações
          </ButtonBase>
          <ButtonBase
            className={activeTab === 'measure' ? classes.segmentedButtonActive : classes.segmentedButton}
            onClick={() => setActiveTab('measure')}
          >
            Medida (Volume / Peso)
          </ButtonBase>
        </div>

        {/* Tab 1: Yield / Fractions */}
        {activeTab === 'yield' && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Quantidade na receita"
                  type="number"
                  inputProps={{ min: 0.01, step: 1, inputMode: 'decimal' }}
                  value={portionCount}
                  onChange={(e) => setPortionCount(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="Ex: 1 dose, 2 fatias"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Rendimento total"
                  type="number"
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
                  value={totalYield}
                  onChange={(e) => setTotalYield(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="Ex: 12 pães, 24 fatias"
                />
              </Grid>
            </Grid>

            {/* Quick shortcuts */}
            <Box mt={2}>
              <Typography className={classes.sectionTitle}>
                Atalhos rápidos de rendimento
              </Typography>
              <Box display="flex" flexWrap="wrap">
                {[2, 4, 6, 8, 10, 12, 15, 20, 24, 30].map((yld) => (
                  <Chip
                    key={yld}
                    label={`1/${yld}`}
                    variant={parseInt(totalYield, 10) === yld && parseFloat(portionCount) === 1 ? 'default' : 'outlined'}
                    color={parseInt(totalYield, 10) === yld && parseFloat(portionCount) === 1 ? 'primary' : 'default'}
                    className={classes.quickChip}
                    onClick={() => {
                      setPortionCount(1);
                      setTotalYield(yld);
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* Tab 2: Weight / Volume Measure */}
        {activeTab === 'measure' && (
          <Box>
            <Grid container spacing={2}>
              {/* Packaging Total */}
              <Grid item xs={7} sm={8}>
                <TextField
                  label="Capacidade da embalagem"
                  type="number"
                  inputProps={{ min: 0.1, step: 'any', inputMode: 'decimal' }}
                  value={containerAmount}
                  onChange={(e) => setContainerAmount(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="Ex: 750 (garrafa) ou 1 (quilo)"
                />
              </Grid>
              <Grid item xs={5} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="calc-container-unit-label">Unidade</InputLabel>
                  <Select
                    labelId="calc-container-unit-label"
                    label="Unidade"
                    value={containerUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      setContainerUnit(newUnit);
                      if (newUnit === 'L' || newUnit === 'ml') {
                        if (recipeUnit === 'g' || recipeUnit === 'kg') setRecipeUnit('ml');
                      } else if (newUnit === 'g' || newUnit === 'kg') {
                        if (recipeUnit === 'L' || recipeUnit === 'ml') setRecipeUnit('g');
                      }
                    }}
                  >
                    <MenuItem value="ml">ml</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Recipe Portion */}
              <Grid item xs={7} sm={8}>
                <TextField
                  label="Quantidade na receita"
                  type="number"
                  inputProps={{ min: 0.1, step: 'any', inputMode: 'decimal' }}
                  value={recipeAmount}
                  onChange={(e) => setRecipeAmount(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  helperText="Ex: 50 (dose) ou 40 (porção)"
                />
              </Grid>
              <Grid item xs={5} sm={4}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="calc-recipe-unit-label">Unidade</InputLabel>
                  <Select
                    labelId="calc-recipe-unit-label"
                    label="Unidade"
                    value={recipeUnit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      setRecipeUnit(newUnit);
                      if (newUnit === 'L' || newUnit === 'ml') {
                        if (containerUnit === 'g' || containerUnit === 'kg') setContainerUnit('L');
                      } else if (newUnit === 'g' || newUnit === 'kg') {
                        if (containerUnit === 'L' || containerUnit === 'ml') setContainerUnit('kg');
                      }
                    }}
                  >
                    <MenuItem value="ml">ml</MenuItem>
                    <MenuItem value="L">L</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {measureResult.incompatible && (
              <Box mt={1}>
                <Typography variant="caption" color="error">
                  As unidades precisam pertencer à mesma grandeza (volume com volume ou peso com peso).
                </Typography>
              </Box>
            )}

            {/* Quick shortcuts for measure */}
            <Box mt={2}>
              <Typography className={classes.sectionTitle}>
                Atalhos rápidos ({isWeightSelected ? 'Peso' : 'Volume'})
              </Typography>
              <Box display="flex" flexWrap="wrap">
                {isWeightSelected ? (
                  [10, 20, 30, 40, 50, 80, 100, 150, 200].map((val) => (
                    <Chip
                      key={val}
                      label={`${val} g`}
                      variant={parseFloat(recipeAmount) === val && recipeUnit === 'g' ? 'default' : 'outlined'}
                      color={parseFloat(recipeAmount) === val && recipeUnit === 'g' ? 'primary' : 'default'}
                      className={classes.quickChip}
                      onClick={() => {
                        setRecipeAmount(val);
                        setRecipeUnit('g');
                      }}
                    />
                  ))
                ) : (
                  [20, 30, 40, 50, 60, 75, 100, 150, 200].map((val) => (
                    <Chip
                      key={val}
                      label={`${val} ml`}
                      variant={parseFloat(recipeAmount) === val && recipeUnit === 'ml' ? 'default' : 'outlined'}
                      color={parseFloat(recipeAmount) === val && recipeUnit === 'ml' ? 'primary' : 'default'}
                      className={classes.quickChip}
                      onClick={() => {
                        setRecipeAmount(val);
                        setRecipeUnit('ml');
                      }}
                    />
                  ))
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Real-time Calculation Summary Box */}
        <Paper variant="outlined" className={classes.summaryBox}>
          <Grid container alignItems="center" justify="space-between" spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" style={{ color: '#64748b', display: 'block' }}>
                Quantidade resultante
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#17497F' }}>
                {activeResult.fractionStr}
                {activeResult.decimal > 0 && (
                  <span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                    (≈ {activeResult.decimal.toFixed(4).replace(/\.?0+$/, '')} un)
                  </span>
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} style={{ textAlign: isMobile ? 'left' : 'right' }}>
              <Typography variant="caption" style={{ color: '#64748b', display: 'block' }}>
                Custo proporcional do insumo
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 700, color: '#0f766e' }}>
                {formattedCurrency(activeResult.cost)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions style={{ padding: isMobile ? 12 : 16 }}>
        <Button onClick={onClose} color="default">
          Cancelar
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          variant="contained"
          disabled={!activeResult || activeResult.decimal <= 0 || activeResult.incompatible}
          style={{ minWidth: 140, fontWeight: 600 }}
        >
          {isEditing ? 'Salvar Ajuste' : 'Adicionar Insumo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceInputCalculatorModal;
